import { useStatsigClient } from '@statsig/expo-bindings';
import { useEffect, useRef } from 'react';

import type {
    ExperimentParameterName,
    ExperimentParameterValue,
} from '../../../../../packages/types/src/common/Experiments';
import { requestBuilder } from '../../http/RequestBuilder';

const useLogExposure = requestBuilder.mutation<
    boolean,
    {
        userId?: string;
        membershipId?: string;
        guideSignUpId?: string;
        unitIdType?: string;
        experimentName: string;
        group: string | null;
        params?: Record<ExperimentParameterName, ExperimentParameterValue>;
    }
>({
    endpoint: '/api/experiment-exposure',
    method: 'POST',
});

/**
 * This hook wraps statsig.getExperiment and a call to log the exposure to
 * the backend.  It returns all the parameters for the experiment group that
 * the user is in.
 */
const useExperimentAndLog = (
    experimentName: string,
    logExposure: boolean = false,
) => {
    const hasLoggedRef = useRef(false);

    const { client: statsig } = useStatsigClient();
    const context = statsig.getContext();
    const customIds = context.user.customIDs;
    const experiment = statsig.getExperiment(experimentName);

    const params = experiment.value as Record<string, ExperimentParameterValue>;

    const { mutate, isPending } = useLogExposure();

    const group = experiment.groupName;
    const userId = context.user.userID;
    const unitIdType = experiment.__evaluation?.id_type;
    const isInExperiment =
        experiment.__evaluation?.is_user_in_experiment ?? false;
    const isExperimentActive =
        experiment.__evaluation?.is_experiment_active ?? false;
    const membershipId = customIds?.membershipId;
    const guideSignUpId = customIds?.guideSignUpId;

    useEffect(() => {
        if (
            logExposure &&
            experiment != null &&
            group != null &&
            isExperimentActive &&
            !isPending &&
            !hasLoggedRef.current
        ) {
            mutate(
                {
                    body: {
                        experimentName,
                        group: isInExperiment
                            ? group
                            : `__${group}_not_in_experiment`,
                        userId,
                        membershipId,
                        guideSignUpId,
                        unitIdType,
                        params,
                    },
                },
                {
                    onSuccess: () => {
                        hasLoggedRef.current = true;
                    },
                },
            );
        }
    }, [
        logExposure,
        experimentName,
        group,
        userId,
        membershipId,
        guideSignUpId,
        unitIdType,
        params,
        isInExperiment,
        isExperimentActive,
        isPending,
    ]);

    return experiment;
};

export default useExperimentAndLog;
